import {
  sidebarOverlayActiveAtom,
  sidebarPanelContentAtom,
  sidebarPanelOverlayLockAtom,
  sidebarPanelPinnedAtom,
  type SidebarPanelContent,
  type SidebarPanelPinnedContent,
} from '@/atoms/sidebarPanelAtoms';
import { IS_SIDEBAR_PANEL_ENABLED } from '@/components/constants/flags';
import {
  SIDEBAR_PANEL_NAV_HOVER_ATTR,
  SIDEBAR_PANEL_NAV_HOVER_VALUE,
} from '@/components/layout/Navigation/sidebar-panel/sidebar-overlay-constants';
import { useAtom, useAtomValue, useStore } from 'jotai';
import { useRouter } from 'next/router';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import {
  cleanupRadixPortalObserver,
  getAnyRadixPortalInDom,
  hasAnyRadixPortalInDom,
  hasOpenOverlayTriggerInSidebar,
  isSessionsOverlaySensitiveContent,
  keepSidebarOpenForOverlayIfNeeded,
  requestOverlayClose,
  shouldKeepOpenForPanelInteraction,
} from './sidebar-panel-overlay-utils';
import {
  clearSharedHideTimeout,
  clearSharedShowTimeout,
  getSharedSidebarElement,
  HIDE_DEBOUNCE_MS,
  setSharedHideTimeout,
  setSharedShowTimeout,
  setSharedSidebarElement,
  SHOW_DEBOUNCE_MS,
} from './sidebar-panel-shared-state';
import { useSelectedSidebarContent } from './use-selected-sidebar-content';
import { getVisibleSidebarPanelContent } from './use-visible-sidebar-panel-content';

const SIDEBAR_PANEL_NAV_HOVER_SELECTOR = `[${SIDEBAR_PANEL_NAV_HOVER_ATTR}="${SIDEBAR_PANEL_NAV_HOVER_VALUE}"]`;

function hasHoveredPanelNavItem(): boolean {
  // During sessions-overlay teardown, hide can be scheduled before the pointer
  // settles on the destination nav item. Treating nav-item hover as "active
  // panel intent" prevents a transient close/reopen flicker in temporary mode.
  const navItems = document.querySelectorAll<HTMLElement>(
    SIDEBAR_PANEL_NAV_HOVER_SELECTOR
  );
  return Array.from(navItems).some((navItem) => navItem.matches(':hover'));
}

export interface UseSidebarPanelReturn {
  /** Current content being displayed (or null if hidden) */
  content: SidebarPanelContent;
  /** Whether the sidebar is currently visible */
  isVisible: boolean;
  /** Whether the feature is enabled */
  isEnabled: boolean;
  /** Whether the sidebar is pinned open (user preference) */
  isPinned: boolean;
  /**
   * Whether the sidebar should render in pinned (inline) mode.
   * True only when isPinned=true AND route has content.
   * When false, sidebar uses floating (overlay) mode even if isPinned=true.
   */
  shouldRenderPinned: boolean;
  /** The content that would be shown based on current route (null if route has no content) */
  selectedContent: SidebarPanelPinnedContent | null;
  /** Ref callback to register the sidebar element */
  sidebarRef: React.RefCallback<HTMLDivElement>;
  /** Handler for when mouse enters a nav item */
  handleNavItemMouseEnter: (content: SidebarPanelContent) => void;
  /** Handler for when mouse leaves a nav item */
  handleNavItemMouseLeave: () => void;
  /** Handler for when mouse enters the sidebar panel */
  handleSidebarMouseEnter: () => void;
  /** Handler for when mouse leaves the sidebar panel */
  handleSidebarMouseLeave: (event: React.MouseEvent) => void;
  /**
   * Handler for when keyboard focus leaves the sidebar panel.
   * Used for skip link accessibility - closes panel when user tabs away.
   */
  handleSidebarBlur: (event: React.FocusEvent) => void;
  /** Toggle pin state */
  togglePin: () => void;
  /** Close the sidebar (unpins if pinned) */
  closeSidebar: () => void;
  /** Clear hover content to revert to route-based content (when pinned) */
  clearHoverContent: () => void;
}

/**
 * Hook for managing the sidebar panel hover and pin state.
 *
 * ## Architecture
 *
 * This hook coordinates hover behavior between NavItem and SidebarPanel components.
 * It uses MODULE-LEVEL state (not per-instance) to allow cross-component communication:
 * - NavItem can start a hide timeout
 * - SidebarPanel can cancel that same timeout
 *
 * ## Features
 *
 * - **Debounced show**: 150ms delay on hover to avoid unnecessary panel updates
 * - **Debounced hide**: 150ms delay to prevent flicker
 * - **Pin/unpin**: Persistent sidebar display with route-based content
 * - **Hover override**: Hovering a different nav item temporarily shows that content
 * - **Radix portal handling**: Dropdowns inside sidebar don't trigger hide
 *
 * ## CSS-Only Mode Switching
 *
 * The sidebar uses CSS `position: fixed` vs `position: relative` to switch between
 * floating (overlay) and pinned (inline) modes. No portal is used for the sidebar
 * itself - the component stays in the same DOM position and CSS controls layout.
 */
export function useSidebarPanel(): UseSidebarPanelReturn {
  const [hoverContent, setHoverContent] = useAtom(sidebarPanelContentAtom);
  const [isPinned, setIsPinned] = useAtom(sidebarPanelPinnedAtom);
  const overlayLockCount = useAtomValue(sidebarPanelOverlayLockAtom);
  const isOverlayLocked = overlayLockCount > 0;
  const store = useStore();
  const router = useRouter();

  // Get content based on current route (replaces pinnedContentAtom)
  const selectedContent = useSelectedSidebarContent();

  const isEnabled = IS_SIDEBAR_PANEL_ENABLED();

  // Ref to store the sidebar element for hover detection in togglePin
  const sidebarElementRef = useRef<HTMLDivElement | null>(null);

  // Track mounted state to guard async callbacks (timeouts, observers)
  // This prevents "can't update unmounted component" warnings
  const isMountedRef = useRef(true);

  const getSidebarElement = useCallback(
    () => sidebarElementRef.current ?? getSharedSidebarElement(),
    []
  );

  // Ref callback for registering the sidebar element
  const sidebarRef = useCallback((element: HTMLDivElement | null) => {
    sidebarElementRef.current = element;
    setSharedSidebarElement(element);
  }, []);

  // Cleanup on unmount to prevent memory leaks from orphaned MutationObservers.
  // When a component using this hook unmounts (e.g., NavItem during navigation),
  // any active MutationObserver must be removed.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      if (getSharedSidebarElement() === sidebarElementRef.current) {
        setSharedSidebarElement(null);
      }
      store.set(sidebarOverlayActiveAtom, false);
      isMountedRef.current = false;
      cleanupRadixPortalObserver();
      clearSharedHideTimeout();
      clearSharedShowTimeout();
    };
  }, [store]);

  // Derive what content to display
  // - Hover content takes precedence when active
  // - When pinned: show route-based selectedContent
  // - When unpinned: show hoverContent only
  const content = getVisibleSidebarPanelContent({
    hoverContent,
    isPinned,
    selectedContent,
  });

  // Derive visibility
  // - When pinned: visible only if content exists (route has mapped content or hovering)
  // - When unpinned: visible only when hover content is set
  const isVisible = isPinned ? content !== null : hoverContent !== null;

  // Keep latest values available to long-lived event listeners.
  const latestContentRef = useRef<SidebarPanelContent>(content);
  const latestHoverContentRef = useRef<SidebarPanelContent>(hoverContent);
  const latestSelectedContentRef = useRef<SidebarPanelPinnedContent | null>(
    selectedContent
  );
  const latestIsEnabledRef = useRef(isEnabled);
  const latestIsPinnedRef = useRef(isPinned);
  latestContentRef.current = content;
  latestHoverContentRef.current = hoverContent;
  latestSelectedContentRef.current = selectedContent;
  latestIsEnabledRef.current = isEnabled;
  latestIsPinnedRef.current = isPinned;

  // Overlay lock: direct state signal from overlay components (e.g. combobox).
  // Replaces flaky DOM-attribute detection for adapted overlays.
  const latestIsOverlayLockedRef = useRef(isOverlayLocked);
  latestIsOverlayLockedRef.current = isOverlayLocked;
  const wasOverlayLockedRef = useRef(false);

  // Effective pinned state: true only when pinned AND route has content.
  // On pages without content (e.g., Tasks), we use overlay/hover behavior
  // even if isPinned=true, so the sidebar acts like temporary overlay.
  const isEffectivelyPinned = isPinned && selectedContent !== null;

  const clearHideTimeout = useCallback(() => {
    clearSharedHideTimeout();
  }, []);

  const clearShowTimeout = useCallback(() => {
    clearSharedShowTimeout();
  }, []);

  // In unpinned mode, route navigation should not leave temporary hover content
  // visible after link clicks (e.g. selecting session from hovercard portal).
  useEffect(() => {
    const events = router?.events;
    if (!events?.on || !events?.off) {
      return;
    }

    const hideUnpinnedHoverPanel = () => {
      clearHideTimeout();
      clearShowTimeout();
      cleanupRadixPortalObserver();
      store.set(sidebarOverlayActiveAtom, false);
      setHoverContent(null);
    };

    const handleRouteChangeStart = () => {
      if (!latestIsEnabledRef.current || latestIsPinnedRef.current) return;
      // Fast path: panel is already hidden in unpinned mode, so no cleanup needed.
      if (latestHoverContentRef.current === null) return;

      const sidebar = getSidebarElement();
      const currentContent = latestContentRef.current;

      // If a sidebar-owned overlay is still open, close it first and wait for portal
      // teardown before unmounting the trigger content to avoid top-left flicker.
      if (
        isSessionsOverlaySensitiveContent(currentContent) &&
        keepSidebarOpenForOverlayIfNeeded({
          sidebar,
          portalTarget: store.get(sidebarOverlayActiveAtom)
            ? getAnyRadixPortalInDom()
            : null,
          onClose: hideUnpinnedHoverPanel,
        })
      ) {
        // Route transitions are panel-managed teardown states, so force-close
        // is allowed here to avoid carrying stale overlays across navigation.
        requestOverlayClose(sidebar);
        return;
      }

      hideUnpinnedHoverPanel();
    };

    events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [
    router,
    clearHideTimeout,
    clearShowTimeout,
    getSidebarElement,
    setHoverContent,
    store,
  ]);

  const scheduleHide = useCallback(() => {
    clearHideTimeout();
    setSharedHideTimeout(
      setTimeout(() => {
        if (!isMountedRef.current) {
          return;
        }

        const currentContent = latestContentRef.current;

        // Direct overlay lock: an overlay component explicitly reported itself as open.
        // Defer hide until the overlay closes (useEffect on lock release will retry).
        if (
          latestIsOverlayLockedRef.current &&
          isSessionsOverlaySensitiveContent(currentContent)
        ) {
          return;
        }

        if (isSessionsOverlaySensitiveContent(currentContent)) {
          const sidebar = getSidebarElement();

          // Race-condition guard:
          // mouseLeave can fire before Radix marks a trigger as data-state="open".
          // Re-check at hide-time so newly opened overlays keep the panel visible.
          //
          // Intentional self-reference: `onClose: scheduleHide` causes the overlay
          // close callback to re-invoke this function. The cycle is:
          //   scheduleHide → detect open overlay → watch → overlay closes → scheduleHide
          // The 150ms debounce (`HIDE_DEBOUNCE_MS`) provides natural back-pressure,
          // preventing tight loops if a trigger rapidly opens/closes.
          const shouldStayOpen = keepSidebarOpenForOverlayIfNeeded({
            sidebar,
            portalTarget: null,
            onClose: scheduleHide,
          });
          if (shouldStayOpen) {
            return;
          }
        }

        // If the panel is still being interacted with (hover/focus), do not hide.
        const shouldRespectFocus =
          latestIsPinnedRef.current && latestSelectedContentRef.current !== null;
        if (
          shouldKeepOpenForPanelInteraction(getSidebarElement(), {
            respectFocus: shouldRespectFocus,
          })
        ) {
          return;
        }

        // Keep temporary panel content stable when cursor is still over a
        // panel nav item (e.g. scribe/evidence) during overlay teardown.
        if (hasHoveredPanelNavItem()) {
          return;
        }

        setHoverContent(null);
      }, HIDE_DEBOUNCE_MS)
    );
  }, [clearHideTimeout, setHoverContent, getSidebarElement]);

  // When the overlay lock releases (overlay component closed), schedule hide
  // so normal leave/hide behavior resumes. If the panel is still hovered,
  // scheduleHide's interaction check will prevent actual hiding.
  useEffect(() => {
    const wasLocked = wasOverlayLockedRef.current;
    wasOverlayLockedRef.current = isOverlayLocked;

    if (wasLocked && !isOverlayLocked) {
      scheduleHide();
    }
  }, [isOverlayLocked, scheduleHide]);

  // Clear hover content immediately
  const clearHoverContent = useCallback(() => {
    setHoverContent(null);
  }, [setHoverContent]);

  const handleNavItemMouseEnter = useCallback(
    (newContent: SidebarPanelContent) => {
      if (!isEnabled || !newContent) return;

      clearHideTimeout();
      clearShowTimeout();
      setSharedShowTimeout(
        setTimeout(() => {
          if (isMountedRef.current) {
            const sidebar = getSidebarElement();
            const currentContent = latestContentRef.current;
            const isSwitchingSessionsToEvidence =
              currentContent === 'sessions' && newContent === 'evidence';
            const isSessionsOverlaySensitiveSwitch =
              isSessionsOverlaySensitiveContent(currentContent) &&
              newContent !== currentContent;

            // Direct overlay lock: an overlay component explicitly reported itself as open.
            // Block the content switch; useEffect on lock release will schedule hide
            // which reverts to route-based content naturally.
            if (latestIsOverlayLockedRef.current && isSessionsOverlaySensitiveSwitch) {
              return;
            }

            const hasSidebarOpenTrigger = hasOpenOverlayTriggerInSidebar(sidebar);
            const shouldDeferForSidebarOwnedPortal =
              isSwitchingSessionsToEvidence &&
              hasSidebarOpenTrigger &&
              hasAnyRadixPortalInDom();
            const shouldDeferForSidebarOverlay =
              isSessionsOverlaySensitiveSwitch && shouldDeferForSidebarOwnedPortal;

            // If a content switch is requested while an overlay is active, defer
            // the switch until overlay teardown to avoid top-left anchor flicker.
            if (shouldDeferForSidebarOverlay) {
              if (
                keepSidebarOpenForOverlayIfNeeded({
                  sidebar,
                  portalTarget: store.get(sidebarOverlayActiveAtom)
                    ? getAnyRadixPortalInDom()
                    : null,
                  onClose: () => {
                    if (!isMountedRef.current) return;
                    store.set(sidebarOverlayActiveAtom, false);
                    setHoverContent(newContent);
                  },
                })
              ) {
                return;
              }
            }

            setHoverContent(newContent);
          }
        }, SHOW_DEBOUNCE_MS)
      );
    },
    [
      isEnabled,
      clearHideTimeout,
      clearShowTimeout,
      getSidebarElement,
      setHoverContent,
      store,
    ]
  );

  const handleNavItemMouseLeave = useCallback(() => {
    if (!isEnabled) return;

    clearShowTimeout();
    // Schedule hide with 150ms debounce.
    // The panel is flush with the nav sidebar, so 150ms provides enough time
    // for the user to move their cursor into the panel. If they do,
    // handleSidebarMouseEnter will cancel this timeout.
    scheduleHide();
  }, [isEnabled, clearShowTimeout, scheduleHide]);

  const handleSidebarMouseEnter = useCallback(() => {
    if (!isEnabled) return;
    clearHideTimeout();
    store.set(sidebarOverlayActiveAtom, false);
    cleanupRadixPortalObserver(); // Clean up if user returns to sidebar from a portal
  }, [isEnabled, clearHideTimeout, store]);

  const handleSidebarMouseLeave = useCallback(
    (event: React.MouseEvent) => {
      if (!isEnabled) return;

      const sidebar = event.currentTarget as HTMLElement | null;
      if (isSessionsOverlaySensitiveContent(latestContentRef.current)) {
        // Direct overlay lock: an overlay component explicitly reported itself as open.
        if (latestIsOverlayLockedRef.current) {
          return;
        }

        // Keep panel open while an overlay (popover/dropdown/hovercard) is active.
        // The observer will schedule hide when the overlay closes naturally
        // (user selects an item, clicks away, or presses Escape).
        if (
          keepSidebarOpenForOverlayIfNeeded({
            sidebar,
            portalTarget: event.relatedTarget,
            onClose: scheduleHide,
          })
        ) {
          return;
        }
      }

      // Use debounce for both pinned and unpinned modes.
      // In pinned mode, this reverts to route content after 150ms.
      // In unpinned mode, this hides the panel after 150ms.
      // The debounce prevents content flicker when the cursor moves from
      // the panel back to the nav item that triggered it — the nav item's
      // mouseEnter cancels the timeout before any content change occurs.
      scheduleHide();
    },
    [isEnabled, scheduleHide]
  );

  /**
   * Handles keyboard focus leaving the sidebar panel.
   * This is the focus counterpart to handleSidebarMouseLeave.
   *
   * Called when using skip links or tabbing through the panel.
   * If focus moves completely outside the panel, schedules hide with debounce.
   *
   * Note: When pinned and showing route content, using the skip link
   * temporarily sets hover content, but tabbing away immediately will
   * revert to route content. This is intentional - skip links provide
   * quick navigation, not permanent content switching.
   */
  const handleSidebarBlur = useCallback(
    (event: React.FocusEvent) => {
      if (!isEnabled) return;

      // Check if focus moved outside the panel
      // relatedTarget is the element receiving focus
      const relatedTarget = event.relatedTarget as Node | null;
      const currentTarget = event.currentTarget;

      // If focus stayed within the panel (or moved to a child), don't close
      if (relatedTarget && currentTarget.contains(relatedTarget)) {
        return;
      }

      // Check if there's an open Radix dropdown/popover trigger inside the panel.
      // When a dropdown is open, focus moves to its portal (outside the panel DOM),
      // but we should keep the panel open until the dropdown closes.
      // This uses the same pattern as handleSidebarMouseLeave.
      const sidebar = event.currentTarget as HTMLElement | null;
      if (isSessionsOverlaySensitiveContent(latestContentRef.current)) {
        // Direct overlay lock: an overlay component explicitly reported itself as open.
        if (latestIsOverlayLockedRef.current) {
          return;
        }

        if (
          keepSidebarOpenForOverlayIfNeeded({
            sidebar,
            portalTarget: event.relatedTarget,
            onClose: scheduleHide,
          })
        ) {
          return;
        }
      }

      // Focus left the panel entirely — cancel any pending timeouts
      clearHideTimeout();
      clearShowTimeout();

      // Always use debounced hide path so hover/focus and overlay guards can
      // prevent premature content reversion in pinned temporary-view mode.
      scheduleHide();
    },
    [isEnabled, scheduleHide, clearHideTimeout, clearShowTimeout]
  );

  const togglePin = useCallback(() => {
    if (isPinned) {
      // Unpinning: transition to floating mode
      // Only preserve hover state if mouse is actually over the panel
      // (e.g., user clicked pin button). For keyboard shortcuts, panel should hide.
      const element = sidebarElementRef.current;
      const isHoveringPanel = element?.matches(':hover') ?? false;

      setIsPinned(false);
      clearHideTimeout();
      clearShowTimeout();

      if (isHoveringPanel) {
        // Mouse is over panel - preserve content so floating panel stays visible
        const contentToPreserve = hoverContent ?? selectedContent;
        if (contentToPreserve) {
          setHoverContent(contentToPreserve);
        }
      } else {
        // Not hovering (e.g., keyboard shortcut) - clear content to hide panel
        setHoverContent(null);
        cleanupRadixPortalObserver();
      }
    } else {
      setIsPinned(true);

      // After DOM updates with new position, check if mouse is still over panel.
      // When transitioning from floating (fixed) to pinned (relative), the panel's
      // viewport position changes. If mouse is no longer over it, clear hover
      // to revert to route-based content.
      requestAnimationFrame(() => {
        if (!isMountedRef.current) return;
        const element = sidebarElementRef.current;
        if (element && !element.matches(':hover')) {
          setHoverContent(null);
        }
      });
    }
  }, [
    isPinned,
    hoverContent,
    selectedContent,
    setIsPinned,
    setHoverContent,
    clearHideTimeout,
    clearShowTimeout,
  ]);

  const closeSidebar = useCallback(() => {
    setIsPinned(false);
    setHoverContent(null);
    store.set(sidebarOverlayActiveAtom, false);
    clearHideTimeout();
    clearShowTimeout();
    cleanupRadixPortalObserver();
  }, [setIsPinned, setHoverContent, clearHideTimeout, clearShowTimeout, store]);

  return {
    content,
    isVisible,
    isEnabled,
    isPinned,
    shouldRenderPinned: isEffectivelyPinned,
    selectedContent,
    sidebarRef,
    handleNavItemMouseEnter,
    handleNavItemMouseLeave,
    handleSidebarMouseEnter,
    handleSidebarMouseLeave,
    handleSidebarBlur,
    togglePin,
    closeSidebar,
    clearHoverContent,
  };
}
